import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Client,
  ClientsListResponse,
  CreateClientInput,
  UpdateClientInput,
} from '@/features/crm/types/client.types';
import type { SalesOrdersListResponse } from '@/features/sales/types/sales-order.types';
import { apiFetch } from '@/lib/api';

/**
 * CRM clients — TanStack Query hooks.
 *
 * Mirrors the catalog/api/brands.ts pattern: 1-based `page`, sortable by
 * `name`, filterable by `isActive` + search + `assignedSellerId`. The
 * mutation hooks invalidate `['clients']` on success.
 *
 * Why a dedicated `clientsKeys` registry: TanStack Query matches keys
 * by reference; centralising them in one place lets pages and forms
 * share the same key space without typo drift.
 */

export type ClientsSortKey = 'name' | 'identificationNumber';
export type SortDirection = 'asc' | 'desc';

export interface ClientsListParams {
  page: number;
  pageSize: number;
  sort?: ClientsSortKey;
  dir?: SortDirection;
  search?: string;
  isActive?: 'active' | 'inactive' | 'all';
  assignedSellerId?: string;
  /** Sprint-2 — filter by negotiation status (semáforo buckets). */
  clientStatus?: 'cerrada' | 'en_negociacion' | 'no_concreto' | 'sin_respuesta';
  // Sprint-3 — CRM filter bar expansion (client request).
  stateId?: string;
  cityId?: string;
  medicalSpecialty?: string;
  interestedProductId?: string;
  /** ISO calendar month `YYYY-MM` (what `<input type="month">` produces). */
  mesIngreso?: string;
  // Slice 0 — origin + capture/close month + segment filters.
  // Same `YYYY-MM` wire shape as `mesIngreso`.
  origen?: string;
  mesCaptacion?: string;
  mesCierre?: string;
  segment?: 'ventas_cerradas' | 'negociacion' | 'no_concretadas';
}

function buildQuery(params: ClientsListParams): string {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page));
  sp.set('pageSize', String(params.pageSize));
  if (params.sort) sp.set('sort', params.sort);
  if (params.dir) sp.set('dir', params.dir);
  if (params.search) sp.set('search', params.search);
  if (params.assignedSellerId) sp.set('assignedSellerId', params.assignedSellerId);
  if (params.clientStatus) sp.set('clientStatus', params.clientStatus);
  // Sprint-3 — new CRM filters. Each is omitted when empty so the
  // API sees a clean URL (the route's Zod schema rejects malformed
  // values with 422 rather than silently treating them as "no
  // filter").
  if (params.stateId) sp.set('stateId', params.stateId);
  if (params.cityId) sp.set('cityId', params.cityId);
  if (params.medicalSpecialty) sp.set('medicalSpecialty', params.medicalSpecialty);
  if (params.interestedProductId) sp.set('interestedProductId', params.interestedProductId);
  if (params.mesIngreso) sp.set('mesIngreso', params.mesIngreso);
  // Slice 0 — origin + capture/close month + segment. Each is
  // omitted when empty so the API sees a clean URL (the route's
  // Zod schema rejects malformed values with 422 rather than
  // silently treating them as "no filter").
  if (params.origen) sp.set('origen', params.origen);
  if (params.mesCaptacion) sp.set('mesCaptacion', params.mesCaptacion);
  if (params.mesCierre) sp.set('mesCierre', params.mesCierre);
  if (params.segment) sp.set('segment', params.segment);
  if (params.isActive === 'inactive') sp.set('isActive', 'false');
  if (params.isActive === 'all') sp.set('includeInactive', 'true');
  return sp.toString();
}

export const clientsKeys = {
  all: ['clients'] as const,
  list: (params: ClientsListParams) => ['clients', 'list', params] as const,
  detail: (id: string) => ['clients', 'detail', id] as const,
};

export function useClients(params: ClientsListParams) {
  return useQuery({
    queryKey: clientsKeys.list(params),
    queryFn: () => apiFetch<ClientsListResponse>(`/clients?${buildQuery(params)}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useClient(id: string | null) {
  return useQuery({
    queryKey: clientsKeys.detail(id ?? ''),
    queryFn: () => apiFetch<Client>(`/clients/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClientInput) =>
      apiFetch<Client>('/clients', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: clientsKeys.all }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateClientInput }) =>
      apiFetch<Client>(`/clients/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: clientsKeys.all });
      qc.invalidateQueries({ queryKey: clientsKeys.detail(id) });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean; id: string }>(`/clients/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: clientsKeys.all }),
  });
}

/**
 * Slice 6 PR#5 (CAP-3) — paginated list of closed sales for one
 * client. The wire reuses SalesOrdersListResponse (same envelope
 * shape as the orders list). The page renders the result inside
 * ClientDetailSheet so the CRM rep sees the historical context.
 */
export interface ClosedSalesListParams {
  clientId: string;
  page: number;
  pageSize: number;
  from?: string;
  to?: string;
}

function buildClosedSalesQuery(params: ClosedSalesListParams): string {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page));
  sp.set('pageSize', String(params.pageSize));
  if (params.from) sp.set('from', params.from);
  if (params.to) sp.set('to', params.to);
  return sp.toString();
}

export const clientsKeysForClosedSales = {
  closedSales: (params: ClosedSalesListParams) => ['clients', 'closed-sales', params] as const,
};

export function useClosedSales(params: ClosedSalesListParams) {
  return useQuery({
    queryKey: clientsKeysForClosedSales.closedSales(params),
    queryFn: () =>
      apiFetch<SalesOrdersListResponse>(
        `/clients/${params.clientId}/closed-sales?${buildClosedSalesQuery(params)}`,
      ),
    enabled: Boolean(params.clientId),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
